import {
  collection,
  addDoc,
  query,
  orderBy,
  getDocs,
  doc,
  updateDoc,
  Timestamp,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../config/firebase';
import { ChatMessage } from './types';

const ALLOWED_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'image/jpeg',
  'image/png',
];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

export async function uploadChatAttachment(
  applicationId: string,
  file: File
): Promise<{ fileName: string; fileUrl: string; fileType: string; fileSize: number; storagePath: string }> {
  if (!ALLOWED_TYPES.includes(file.type)) {
    throw new Error('Filtypen stöds inte. Tillåtna: PDF, DOCX, PPTX, JPEG, PNG');
  }
  if (file.size > MAX_FILE_SIZE) {
    throw new Error('Filen är för stor. Max 10 MB.');
  }

  const storagePath = `chat-attachments/${applicationId}/${Date.now()}_${file.name}`;
  const storageRef = ref(storage, storagePath);
  await uploadBytes(storageRef, file);
  const fileUrl = await getDownloadURL(storageRef);

  return {
    fileName: file.name,
    fileUrl,
    fileType: file.type,
    fileSize: file.size,
    storagePath,
  };
}

export async function sendMessage(
  applicationId: string,
  message: {
    text: string;
    senderUid: string;
    senderName: string;
    senderRole: 'owner' | 'brfadmin';
    attachment?: ChatMessage['attachment'];
  }
): Promise<void> {
  const messagesRef = collection(db, 'applications', applicationId, 'messages');
  await addDoc(messagesRef, {
    text: message.text,
    senderUid: message.senderUid,
    senderName: message.senderName,
    senderRole: message.senderRole,
    createdAt: Timestamp.now(),
    ...(message.attachment ? { attachment: message.attachment } : {}),
  });
}

export async function fetchMessages(
  applicationId: string
): Promise<ChatMessage[]> {
  const messagesRef = collection(db, 'applications', applicationId, 'messages');
  const q = query(messagesRef, orderBy('createdAt', 'asc'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({
    id: d.id,
    ...d.data(),
  })) as ChatMessage[];
}

export async function markAsRead(
  applicationId: string,
  uid: string
): Promise<void> {
  const appRef = doc(db, 'applications', applicationId);
  await updateDoc(appRef, {
    [`chatUnread.${uid}`]: 0,
  });
}
